import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Bot, DollarSign, FileText, Power, PowerOff, Plus, X, Download, AlertTriangle, Filter } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  inactive: "bg-zinc-100 text-zinc-700",
  deprecated: "bg-amber-100 text-amber-800",
  draft: "bg-blue-100 text-blue-800",
};

export default function AIGovernance() {
  const [models, setModels] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [budgetAlerts, setBudgetAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("models");
  const [showCreate, setShowCreate] = useState(false);
  const [modelForm, setModelForm] = useState({ model_identifier: "", provider: "", version: "", assigned_modules: "", monthly_budget_usd: "", hard_stop: false, fallback_degrade: false });
  const [saving, setSaving] = useState(false);
  // Audit log filters
  const [auditFilters, setAuditFilters] = useState({ module: "", user_id: "", model_id: "", date_from: "", date_to: "" });

  const load = () => {
    Promise.all([
      api.get("/ai-models").then((r) => setModels(r.data)).catch(() => {}),
      api.get("/ai-models/budgets").then((r) => setBudgets(r.data)).catch(() => {}),
      api.get("/ai-audit-logs").then((r) => setAuditLog(r.data)).catch(() => {}),
      api.get("/ai-models/budget-alerts").then((r) => setBudgetAlerts(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadFilteredAuditLog = async () => {
    const params = new URLSearchParams();
    if (auditFilters.module) params.append("module", auditFilters.module);
    if (auditFilters.user_id) params.append("user_id", auditFilters.user_id);
    if (auditFilters.model_id) params.append("model_id", auditFilters.model_id);
    if (auditFilters.date_from) params.append("date_from", auditFilters.date_from);
    if (auditFilters.date_to) params.append("date_to", auditFilters.date_to);
    try {
      const r = await api.get(`/ai-audit-logs?${params.toString()}`);
      setAuditLog(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleModel = async (id, currentStatus) => {
    const next = currentStatus === "active" ? "deprecated" : "active";
    try {
      await api.patch(`/ai-models/${id}/status`, { status: next });
      toast.success(`Model ${next === "active" ? "activated" : "deprecated"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createModel = async () => {
    setSaving(true);
    try {
      const payload = {
        model_identifier: modelForm.model_identifier,
        provider: modelForm.provider,
        version: modelForm.version,
        assigned_modules: modelForm.assigned_modules.split(",").map((s) => s.trim()).filter(Boolean),
        monthly_budget_usd: modelForm.monthly_budget_usd ? parseFloat(modelForm.monthly_budget_usd) : null,
        hard_stop: modelForm.hard_stop,
        fallback_degrade: modelForm.fallback_degrade,
      };
      await api.post("/ai-models", payload);
      toast.success("Model registered");
      setShowCreate(false);
      setModelForm({ model_identifier: "", provider: "", version: "", assigned_modules: "", monthly_budget_usd: "", hard_stop: false, fallback_degrade: false });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportAuditCsv = () => {
    if (auditLog.length === 0) return;
    const headers = ["Module", "Operation", "Outcome", "User ID", "Model Registry ID", "Input Tokens", "Output Tokens", "Estimated Cost USD", "Timestamp"];
    const rows = auditLog.map((e) => [
      e.platform_module || "",
      e.operation_type || "",
      e.outcome || "success",
      e.user_id || "",
      e.model_registry_id || "",
      e.input_tokens || 0,
      e.output_tokens || 0,
      e.estimated_cost_usd || 0,
      e.created_at || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ai-audit-log.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Audit log exported as CSV");
  };

  // Get unique modules for filter dropdown
  const uniqueModules = [...new Set(auditLog.map((e) => e.platform_module).filter(Boolean))];

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Settings</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">AI Governance</h1>
          <p className="text-zinc-500 mt-2 text-sm">Model registry, budget tracking and audit logs for AI operations.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> Register model
        </button>
      </div>

      {/* Budget Alerts */}
      {budgetAlerts.length > 0 && (
        <div className="mt-4 space-y-2">
          {budgetAlerts.map((alert) => (
            <div key={alert.model_id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${alert.exceeded ? "bg-red-50 text-red-800 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
              <AlertTriangle size={16} />
              <span className="font-medium">{alert.model_identifier}:</span>
              <span>{alert.message}</span>
              <span className="ml-auto text-xs">{alert.utilization_pct}% used</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        {[
          { id: "models", label: "Models", icon: Bot },
          { id: "budgets", label: "Budgets", icon: DollarSign },
          { id: "audit", label: "Audit Log", icon: FileText },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "models" ? (
        models.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Bot size={26} className="mx-auto mb-3" />
            <p className="text-sm">No AI models registered.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {models.map((m) => (
              <div key={m.id} className="border border-zinc-200 bg-white rounded-lg p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium">{m.model_identifier}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{m.provider} | v{m.version}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[m.status] || STATUS_STYLE.inactive}`}>{m.status}</span>
                </div>
                {(m.assigned_modules || []).length > 0 && (
                  <p className="text-xs text-zinc-500 mt-2">Modules: {m.assigned_modules.join(", ")}</p>
                )}
                <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-100">
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Budget</p>
                    <p className="text-sm font-medium">${m.monthly_budget_usd?.toFixed(2) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Hard Stop</p>
                    <p className="text-sm font-medium">{m.hard_stop ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-zinc-400">Fallback</p>
                    <p className="text-sm font-medium">{m.fallback_degrade ? "Yes" : "—"}</p>
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button onClick={() => toggleModel(m.id, m.status)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${m.status === "active" ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100"}`}>
                    {m.status === "active" ? <><PowerOff size={12} /> Deactivate</> : <><Power size={12} /> Activate</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === "budgets" ? (
        budgets.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <DollarSign size={26} className="mx-auto mb-3" />
            <p className="text-sm">No budget data available.</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgets.map((b) => {
              const usedPct = b.monthly_budget_usd ? Math.min(100, Math.round((b.spent_usd / b.monthly_budget_usd) * 100)) : 0;
              return (
                <div key={b.model_id} className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-sm font-medium">{b.model_identifier}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{b.provider} · {b.status}{b.hard_stop ? " · Hard stop" : ""}</p>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-500">${b.spent_usd?.toFixed(2)} spent</span>
                      <span className="text-zinc-400">{b.monthly_budget_usd ? `$${b.monthly_budget_usd.toFixed(2)} budget` : "No budget"}</span>
                    </div>
                    {b.monthly_budget_usd ? (
                      <>
                        <div className="w-full bg-zinc-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${usedPct > 90 ? "bg-red-400" : usedPct > 70 ? "bg-amber-400" : "bg-emerald-400"}`}
                            style={{ width: `${usedPct}%` }} />
                        </div>
                        <p className="text-[10px] text-zinc-400 mt-1">{usedPct}% utilized</p>
                      </>
                    ) : (
                      <p className="text-[10px] text-zinc-400 mt-1">No budget limit set</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="mt-6">
          {/* Audit Log Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-end">
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 mb-1">Module</label>
              <select value={auditFilters.module} onChange={(e) => setAuditFilters({ ...auditFilters, module: e.target.value })}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm">
                <option value="">All modules</option>
                {uniqueModules.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 mb-1">User</label>
              <input type="text" placeholder="User ID" value={auditFilters.user_id} onChange={(e) => setAuditFilters({ ...auditFilters, user_id: e.target.value })}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 mb-1">Model</label>
              <select value={auditFilters.model_id} onChange={(e) => setAuditFilters({ ...auditFilters, model_id: e.target.value })}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm">
                <option value="">All models</option>
                {models.map((m) => <option key={m.id} value={m.id}>{m.model_identifier}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 mb-1">Date from</label>
              <input type="date" value={auditFilters.date_from} onChange={(e) => setAuditFilters({ ...auditFilters, date_from: e.target.value })}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 mb-1">Date to</label>
              <input type="date" value={auditFilters.date_to} onChange={(e) => setAuditFilters({ ...auditFilters, date_to: e.target.value })}
                className="border border-zinc-300 rounded-md px-3 py-1.5 text-sm" />
            </div>
            <button onClick={loadFilteredAuditLog}
              className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 transition-colors">
              <Filter size={12} /> Apply filters
            </button>
            <div className="ml-auto">
              <button onClick={exportAuditCsv}
                className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 transition-colors">
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>
          {auditLog.length === 0 ? (
            <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <FileText size={26} className="mx-auto mb-3" />
              <p className="text-sm">No audit log entries.</p>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                <span className="col-span-2">Module</span>
                <span className="col-span-2">Operation</span>
                <span className="col-span-1">Outcome</span>
                <span className="col-span-2">Tokens</span>
                <span className="col-span-2">Cost</span>
                <span className="col-span-3">Timestamp</span>
              </div>
              {auditLog.map((entry) => (
                <div key={entry.id} className="grid grid-cols-12 items-center px-6 py-3 border-b border-zinc-50">
                  <span className="col-span-2 text-sm font-medium">{entry.platform_module}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{entry.operation_type}</span>
                  <span className="col-span-1 text-sm text-zinc-600">{entry.outcome || "success"}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{((entry.input_tokens || 0) + (entry.output_tokens || 0)).toLocaleString()}</span>
                  <span className="col-span-2 text-sm text-zinc-600">${(entry.estimated_cost_usd || 0).toFixed(4)}</span>
                  <span className="col-span-3 text-xs text-zinc-400">{entry.created_at}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Model Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Register AI model</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={modelForm.model_identifier} onChange={(e) => setModelForm({ ...modelForm, model_identifier: e.target.value })} placeholder="Model identifier (e.g. gpt-4o)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={modelForm.provider} onChange={(e) => setModelForm({ ...modelForm, provider: e.target.value })} placeholder="Provider (e.g. openai, anthropic)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={modelForm.version} onChange={(e) => setModelForm({ ...modelForm, version: e.target.value })} placeholder="Version (e.g. 1.0)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={modelForm.assigned_modules} onChange={(e) => setModelForm({ ...modelForm, assigned_modules: e.target.value })} placeholder="Assigned modules (comma separated, at least one)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={modelForm.monthly_budget_usd} onChange={(e) => setModelForm({ ...modelForm, monthly_budget_usd: e.target.value })} placeholder="Monthly budget USD (optional)" type="number" step="0.01"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={modelForm.hard_stop} onChange={(e) => setModelForm({ ...modelForm, hard_stop: e.target.checked })}
                    className="rounded border-zinc-300" />
                  Hard stop at budget
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={modelForm.fallback_degrade} onChange={(e) => setModelForm({ ...modelForm, fallback_degrade: e.target.checked })}
                    className="rounded border-zinc-300" />
                  Fallback degrade
                </label>
              </div>
              <button onClick={createModel} disabled={!modelForm.model_identifier || !modelForm.provider || !modelForm.assigned_modules || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Registering..." : "Register model"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
